import { Link } from 'react-router-dom';
import {
  ArrowDownRight, ArrowUpRight, Circle, Eye, FileText, Globe, Home, Inbox, Mail,
  MessageCircle, Minus, Monitor, MousePointerClick, Phone, ShoppingCart, Smartphone, Tablet,
  type LucideIcon,
} from 'lucide-react';
import { dashboardQuickActions } from '../../lib/panelNavigation';
import type {
  CollectionTypeDef, FormSubmission, PublishLog, SubmissionStats, VisitStats,
} from '../../lib/types';

const EVENT_LABEL: Record<string, string> = {
  pageview: 'Visita',
  whatsapp_click: 'Clic a WhatsApp',
  phone_tap: 'Toque al teléfono',
  form_submit: 'Formulario enviado',
  catalog_whatsapp: 'Pedido por WhatsApp',
  link_tap: 'Toque a un enlace',
  click: 'Clic',
};

const EVENT_ICON: Record<string, LucideIcon> = {
  pageview: Eye,
  whatsapp_click: MessageCircle,
  phone_tap: Phone,
  form_submit: Mail,
  catalog_whatsapp: ShoppingCart,
  link_tap: MousePointerClick,
  click: MousePointerClick,
};

const DEVICE_INFO: Record<string, { label: string; icon: LucideIcon }> = {
  desktop: { label: 'Escritorio', icon: Monitor },
  mobile: { label: 'Móvil', icon: Smartphone },
  tablet: { label: 'Tablet', icon: Tablet },
};

function monthLabel(key: string): string {
  const [year, month] = key.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, 1))
    .toLocaleDateString('es-MX', { month: 'short', timeZone: 'UTC' })
    .replace('.', '');
}

function kindIcon(kind: string): LucideIcon {
  return kind === 'item' ? Home : FileText;
}

function activityPhrase(eventKey: string, label: string): string {
  switch (eventKey) {
    case 'pageview': return `Vieron ${label}`;
    case 'whatsapp_click': return `Clic a WhatsApp · ${label}`;
    case 'phone_tap': return `Toque al teléfono · ${label}`;
    case 'form_submit': return `Formulario enviado · ${label}`;
    case 'catalog_whatsapp': return `Pedido por WhatsApp · ${label}`;
    case 'link_tap': return `Toque a un enlace · ${label}`;
    default: return `${EVENT_LABEL[eventKey] ?? eventKey} · ${label}`;
  }
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.round((Date.now() - then) / 60000);
  if (minutes < 1) return 'Justo ahora';
  if (minutes < 60) return `Hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `Hace ${hours} h`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'Ayer' : `Hace ${days} días`;
}

export function DashboardMetricsPanel({ stats, visits }: { stats: SubmissionStats | null; visits: VisitStats | null }) {
  if (!stats) {
    return (
      <div className="bg-ui-surface border border-ui-border rounded-xl p-6 animate-pulse">
        <div className="h-3 w-40 bg-ui-border rounded" />
        <div className="h-9 w-56 bg-ui-border rounded mt-4" />
      </div>
    );
  }

  const { thisMonth, lastMonth, total, deltaPct, monthly } = stats;
  const maxCount = Math.max(1, ...monthly.map((entry) => entry.count));

  return (
    <section className="bg-ui-surface border border-ui-border rounded-xl p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div>
        <p className="text-xs font-medium text-ui-text-muted uppercase tracking-wider">Tu sitio en números</p>
        <p className="mt-2 text-2xl sm:text-3xl font-semibold text-ui-text leading-tight">
          {thisMonth > 0 ? (
            <>Este mes tu sitio te trajo <span className="text-ui-accent">{thisMonth}</span> {thisMonth === 1 ? 'contacto' : 'contactos'}.</>
          ) : (
            <>Aún sin contactos este mes.</>
          )}
        </p>
        {thisMonth === 0 && total > 0 && (
          <p className="mt-1 text-sm text-ui-text-subtle">Llevas {total} en total. Publica novedades para reactivar las consultas.</p>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <Stat label="Este mes" value={thisMonth} />
        <Stat label="Mes anterior" value={lastMonth} delta={deltaPct} />
        <Stat label="Total" value={total} />
      </div>

      <div>
        <p className="text-xs text-ui-text-subtle mb-2">Contactos por mes</p>
        <div className="flex items-end gap-2 h-20">
          {monthly.map((entry, index) => {
            const isCurrent = index === monthly.length - 1;
            const height = Math.round((entry.count / maxCount) * 100);
            return (
              <div key={entry.month} className="flex-1 flex flex-col items-center gap-1.5">
                <div className="w-full flex items-end h-16">
                  <div
                    className={`w-full rounded-t transition-all ${isCurrent ? 'bg-ui-accent' : 'bg-ui-border-strong'}`}
                    style={{ height: `${Math.max(height, entry.count > 0 ? 8 : 2)}%` }}
                    title={`${entry.count}`}
                  />
                </div>
                <span className={`text-xs ${isCurrent ? 'text-ui-accent' : 'text-ui-text-subtle'}`}>
                  {monthLabel(entry.month)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {visits?.configured && (
        <div className="pt-5 border-t border-ui-border space-y-6">
          <div>
            <p className="text-xs text-ui-text-subtle mb-3">Tráfico del sitio este mes</p>
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              <Stat label="Visitas" value={visits.thisMonthVisits} delta={visits.deltaPct} />
              <Stat label="WhatsApp" value={visits.whatsappThisMonth} />
              <Stat label="Acciones" value={visits.conversionsThisMonth} />
            </div>
          </div>

          {visits.topPages.length > 0 && (
            <div>
              <p className="text-xs text-ui-text-subtle mb-2">Lo más visto este mes</p>
              <div className="space-y-1.5">
                {visits.topPages.map((page, index) => {
                  const max = visits.topPages[0].count || 1;
                  const KindIcon = kindIcon(page.kind);
                  return (
                    <div key={`${page.label}-${index}`} className="flex items-center gap-2.5">
                      <KindIcon size={15} strokeWidth={1.75} className="text-ui-text-subtle flex-shrink-0" />
                      <span className="text-sm text-ui-text flex-1 truncate" title={page.label}>{page.label}</span>
                      <div className="w-16 h-1.5 bg-ui-bg rounded-full overflow-hidden flex-shrink-0">
                        <div className="h-full bg-ui-accent rounded-full" style={{ width: `${Math.round((page.count / max) * 100)}%` }} />
                      </div>
                      <span className="text-xs text-ui-text-muted tabular-nums w-7 text-right flex-shrink-0">{page.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(visits.conversions.length > 0 || visits.devices.length > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {visits.conversions.length > 0 && (
                <div>
                  <p className="text-xs text-ui-text-subtle mb-2">Cómo te contactan</p>
                  <div className="space-y-1.5">
                    {visits.conversions.map((conversion) => {
                      const Icon = EVENT_ICON[conversion.ev] ?? Circle;
                      return (
                        <div key={conversion.ev} className="flex items-center gap-2 text-sm">
                          <Icon size={15} strokeWidth={1.75} className="text-ui-text-muted flex-shrink-0" />
                          <span className="text-ui-text flex-1 truncate">{EVENT_LABEL[conversion.ev] ?? conversion.ev}</span>
                          <span className="text-ui-text-muted tabular-nums">{conversion.count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {visits.devices.length > 0 && <DeviceSplit devices={visits.devices} />}
            </div>
          )}

          {visits.recent.length > 0 && (
            <div>
              <p className="text-xs text-ui-text-subtle mb-2">Actividad reciente</p>
              <div className="space-y-1.5">
                {visits.recent.map((activity, index) => {
                  const Icon = EVENT_ICON[activity.ev] ?? Circle;
                  const device = DEVICE_INFO[activity.device];
                  const DeviceIcon = device?.icon;
                  return (
                    <div key={`${activity.ts}-${index}`} className="flex items-center gap-2 text-sm">
                      <Icon size={15} strokeWidth={1.75} className="text-ui-text-muted flex-shrink-0" />
                      <span className="text-ui-text flex-1 truncate">{activityPhrase(activity.ev, activity.label)}</span>
                      {DeviceIcon && <DeviceIcon size={14} strokeWidth={1.75} className="text-ui-text-subtle flex-shrink-0" aria-label={device.label} />}
                      <span className="text-xs text-ui-text-subtle whitespace-nowrap flex-shrink-0">{relativeTime(activity.ts)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export function UnreadLeadsNotice({ unread }: { unread: number }) {
  if (unread <= 0) return null;

  return (
    <Link
      to="/submissions"
      className="flex items-center gap-3 bg-ui-accent/10 border border-ui-accent/40 rounded-lg px-4 py-3 hover:border-ui-accent transition-colors group"
    >
      <Inbox size={18} className="text-ui-accent flex-shrink-0" />
      <div className="flex-1">
        <p className="text-sm font-medium text-ui-text">
          Tienes {unread} {unread === 1 ? 'cliente nuevo' : 'clientes nuevos'} esperando
        </p>
        <p className="text-sm text-ui-text-subtle mt-0.5">
          {unread === 1 ? 'Un mensaje sin leer' : `${unread} mensajes sin leer`} — responde rápido para no perder la venta.
        </p>
      </div>
      <span className="text-ui-accent group-hover:translate-x-0.5 transition-transform">→</span>
    </Link>
  );
}

export function DashboardQuickActions({ collectionTypes }: { collectionTypes: CollectionTypeDef[] }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {dashboardQuickActions(collectionTypes).map(({ label, to, description }) => (
        <Link
          key={to}
          to={to}
          className="bg-ui-surface border border-ui-border rounded-lg px-4 py-3 hover:border-ui-accent transition-colors group"
        >
          <p className="text-sm font-medium text-ui-text group-hover:text-ui-accent transition-colors">{label}</p>
          <p className="text-sm text-ui-text-subtle mt-0.5">{description}</p>
        </Link>
      ))}
    </div>
  );
}

export function LiveSiteLink({ siteUrl }: { siteUrl: string | null }) {
  if (!siteUrl) return null;

  return (
    <a
      href={siteUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-between bg-ui-surface border border-ui-border rounded-lg px-4 py-3 hover:border-ui-accent transition-colors group"
    >
      <div>
        <p className="text-sm font-medium text-ui-text group-hover:text-ui-accent transition-colors">Ver sitio en vivo</p>
        <p className="text-xs text-ui-text-subtle mt-0.5">{siteUrl}</p>
      </div>
      <span className="text-ui-text-subtle group-hover:text-ui-accent transition-colors text-lg">↗</span>
    </a>
  );
}

export function LastDeploySection({ lastDeploy }: { lastDeploy: PublishLog | null }) {
  if (!lastDeploy) return null;

  return (
    <section>
      <h2 className="text-sm font-medium text-ui-text-muted uppercase tracking-wider mb-3">
        Última publicación
      </h2>
      <div className="bg-ui-surface rounded-lg border border-ui-border p-4">
        <div className="flex items-center gap-3">
          <span
            className={`w-2 h-2 rounded-full flex-shrink-0 ${
              lastDeploy.status === 'success'
                ? 'bg-ui-success'
                : lastDeploy.status === 'error'
                ? 'bg-ui-danger'
                : 'bg-ui-warning'
            }`}
          />
          <span className="text-sm text-ui-text">
            {lastDeploy.status === 'success' ? 'Exitosa' : lastDeploy.status === 'error' ? 'Con error' : 'En proceso'}
          </span>
          <span className="text-xs text-ui-text-subtle">por {lastDeploy.triggeredBy}</span>
        </div>
        {lastDeploy.errorMsg && (
          <p className="mt-2 text-sm text-ui-danger font-mono">{lastDeploy.errorMsg}</p>
        )}
        <p className="mt-2 text-sm text-ui-text-subtle">
          Para publicar cambios ve a{' '}
          <Link to="/site?tab=paginas" className="text-ui-accent hover:underline">
            Sitio
          </Link>
          .
        </p>
      </div>
    </section>
  );
}

export function RecentSubmissionsSection({ submissions }: { submissions: FormSubmission[] }) {
  return (
    <section>
      <h2 className="text-sm font-medium text-ui-text-muted uppercase tracking-wider mb-3">
        Últimas consultas del sitio
      </h2>
      <div className="bg-ui-surface rounded-lg border border-ui-border divide-y divide-ui-border">
        {submissions.map((submission) => {
          const payload = JSON.parse(submission.payloadJson) as { name?: string; email?: string; message?: string };
          return (
            <div key={submission.id} className="px-4 py-3">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm text-ui-text">{payload.name ?? '—'}</span>
                  <span className="ml-2 text-xs text-ui-text-subtle">{payload.email}</span>
                </div>
                <span className={`text-xs ${submission.emailSent ? 'text-ui-success' : 'text-ui-warning'}`}>
                  {submission.emailSent ? '✓ enviado' : '⚠ sin enviar'}
                </span>
              </div>
              {payload.message && (
                <p className="mt-1 text-sm text-ui-text-subtle truncate">{payload.message}</p>
              )}
            </div>
          );
        })}
        {submissions.length === 0 && (
          <p className="px-4 py-6 text-sm text-ui-text-subtle text-center">
            Las consultas enviadas desde los formularios de tu sitio aparecerán aquí.
          </p>
        )}
      </div>
    </section>
  );
}

function DeviceSplit({ devices }: { devices: { device: string; count: number }[] }) {
  const total = devices.reduce((sum, device) => sum + device.count, 0) || 1;

  return (
    <div>
      <p className="text-xs text-ui-text-subtle mb-2">¿Desde dónde te ven?</p>
      <div className="space-y-1.5">
        {devices.map((device) => {
          const info = DEVICE_INFO[device.device] ?? { label: device.device, icon: Globe };
          const Icon = info.icon;
          return (
            <div key={device.device} className="flex items-center gap-2 text-sm">
              <Icon size={15} strokeWidth={1.75} className="text-ui-text-subtle flex-shrink-0" />
              <span className="text-ui-text flex-1 truncate">{info.label}</span>
              <span className="text-ui-text-muted tabular-nums">{Math.round((device.count / total) * 100)}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value, delta }: { label: string; value: number; delta?: number | null }) {
  return (
    <div className="bg-ui-bg border border-ui-border rounded-lg px-3 py-2.5 sm:px-4 sm:py-3">
      <p className="text-xs text-ui-text-subtle truncate">{label}</p>
      <div className="flex items-baseline gap-1.5 mt-0.5 sm:mt-1 flex-wrap">
        <span className="text-xl sm:text-2xl font-semibold text-ui-text">{value}</span>
        {delta !== undefined && <DeltaBadge delta={delta} />}
      </div>
    </div>
  );
}

function DeltaBadge({ delta }: { delta: number | null }) {
  if (delta === null) return <span className="text-xs text-ui-text-subtle">sin base</span>;
  if (delta === 0) {
    return (
      <span className="flex items-center gap-0.5 text-xs text-ui-text-subtle">
        <Minus size={11} /> igual
      </span>
    );
  }

  const isPositive = delta > 0;
  return (
    <span className={`flex items-center gap-0.5 text-xs ${isPositive ? 'text-ui-success' : 'text-ui-danger'}`}>
      {isPositive ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
      {Math.abs(delta)}%
    </span>
  );
}
